import type { CompanyProfile } from "../types";
import type {
  EffectivePurchaseOrderDraft,
  PurchaseOrderHeader,
} from "./purchaseOrderEdits";
import { formatJpDateFixed, transitionalSummary } from "./invoiceTransition";
import {
  JP_REGISTRATION_CONCEPT,
  type RegistrationConcept,
} from "./registrationLabel";

import { t } from "../lib/i18n";
import { money } from "./money";

/** HTML特殊文字をエスケープ（XSS・レイアウト崩れ防止）。 */
export function escapeHtml(value: string | number): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** その発注書の通貨で金額を整形する。通貨は `draft.currency`（店の通貨）。 */
function amountIn(draft: EffectivePurchaseOrderDraft) {
  return (amount: string | number): string => money(amount, draft.currency);
}

function lineRows(draft: EffectivePurchaseOrderDraft, hasTax: boolean): string {
  const yen = amountIn(draft);
  return draft.lines
    .map((l) => {
      const unit = l.is_estimated ? `${yen(l.unit_price)}<span class="est">${t("（目安）")}</span>` : yen(l.unit_price);
      const taxCell = hasTax ? `
        <td>${escapeHtml(l.tax_rate_name ?? "")}</td>` : "";
      return `<tr>
        <td>${escapeHtml(l.sku ?? "—")}</td>
        <td>${escapeHtml(l.title ?? "—")}</td>
        <td class="num">${escapeHtml(l.available)}</td>
        <td class="num">${escapeHtml(l.recommended_qty)}</td>
        <td class="num">${unit}</td>${taxCell}
        <td class="num">${yen(l.line_amount)}</td>
      </tr>`;
    })
    .join("");
}

/** 発注元（自社プロフィール）ブロック。未設定時は控えめな注意書きにフォールバック。 */
function issuerBlock(
  company: CompanyProfile | undefined,
  registration: RegistrationConcept | null,
): string {
  if (!company?.company_name) {
    return `<div class="warn">${t("発注元 未設定")}</div>`;
  }
  const rows: string[] = [`<div class="name">${escapeHtml(company.company_name)}</div>`];
  const addr = [
    company.company_postal_code ? `〒${escapeHtml(company.company_postal_code)}` : "",
    company.company_address ? escapeHtml(company.company_address) : "",
  ]
    .filter(Boolean)
    .join(" ");
  if (addr) rows.push(`<div>${addr}</div>`);
  if (company.company_tel) rows.push(`<div>TEL: ${escapeHtml(company.company_tel)}</div>`);
  // 登録番号の行は国の概念があるときだけ（US・カタログ外は値が残っていても出さない）。
  // ラベル＝JP は従来の「登録番号」・非 JP はその国の呼び名（"VAT number" 等）。
  if (company.company_registration_no && registration) {
    const label = registration.isJapanInvoiceSystem
      ? t("登録番号")
      : escapeHtml(registration.label);
    rows.push(
      `<div>${label}: ${escapeHtml(company.company_registration_no)}</div>`,
    );
  }
  return rows.join("");
}

/**
 * 1仕入先分の発注書ドラフトを、ブラウザ印刷（PDF保存）向けの完結したHTML文書に変換する。
 * A4・発注書レイアウト。発注元（自社プロフィール）をヘッダに、仕入先の適格請求書登録状況を宛先欄に併記し、税区分別（軽減8%/標準10%）集計を付ける。日本語はブラウザ標準フォントで正しく描画される。
 */
export function buildPurchaseOrderHtml(
  draft: EffectivePurchaseOrderDraft,
  today: string,
  company?: CompanyProfile,
  header?: PurchaseOrderHeader,
  destination?: string,
  options?: {
    /**
     * 適格請求書の経過措置の脚注を出すか（既定 true）。
     * 🔴 経過措置は日本の制度＝JP 以外の店の PDF に出すと、存在しない制度の
     * 控除額を仕入先向けの書類に断言することになる。呼び出し側が店の国で決める。
     */
    showInvoiceTransitionNote?: boolean;
    /**
     * 登録番号のラベル（国の概念・Step 3）。未指定＝JP（後方互換）・null＝
     * 概念なし（US 等＝発注元・仕入先の登録番号行を出さない）。
     */
    registration?: RegistrationConcept | null;
  },
): string {
  const yen = amountIn(draft);
  const registration =
    options?.registration === undefined
      ? JP_REGISTRATION_CONCEPT
      : options.registration;
  // 🔴 税率行が1つも無い店（US・カタログ外の国＝Step 4）は税区分列・税集計・
  //    「税込」の文言を書類から消す＝「税の行が存在しない」が正しい形。
  const hasTax = draft.tax_groups.length > 0;
  const issueDate = header?.issueDate || today;
  const metaExtra = [
    header?.poNumber
      ? `<div>${t("発注番号")}: ${escapeHtml(header.poNumber)}</div>`
      : "",
    header?.desiredDeliveryDate
      ? `<div>${t("希望納品日")}: ${escapeHtml(header.desiredDeliveryDate)}</div>`
      : "",
    destination ? `<div>${t("配送先")}: ${escapeHtml(destination)}</div>` : "",
  ].join("");
  const noteBlock = header?.note
    ? `<div class="notebox"><div class="notebox-label">${t("備考")}</div>${escapeHtml(header.note)}</div>`
    : "";

  // 仕入先の登録番号行。JP＝従来どおり（未登録は経過措置の前提なので警告を出す）。
  // 非 JP ＝値があるときだけその国のラベルで出す（未登録の警告はしない＝JP の
  // 控除制度が無い国で「未登録」を問題であるかのように断言しない）。概念なし＝行ごと無し。
  const invoiceLine = !registration
    ? ""
    : draft.invoice_registration_no
      ? `<div>${
          registration.isJapanInvoiceSystem
            ? t("仕入先 登録番号")
            : escapeHtml(registration.label)
        }: <strong>${escapeHtml(draft.invoice_registration_no)}</strong></div>`
      : registration.isJapanInvoiceSystem
        ? `<div class="warn">${t("仕入先 登録番号 未登録")}</div>`
        : "";

  const estimatedNote = draft.has_estimated_lines
    ? `<p class="note">${t("※「目安」表示の仕入単価は、Shopifyに仕入原価が未設定のため販売価格を暫定使用しています。")}</p>`
    : "";

  // 未登録仕入先は経過措置の対象。事実の脚注として控除可能・控除不可の消費税額を小さく注記する。
  let transitionalNote = "";
  if ((options?.showInvoiceTransitionNote ?? true) && !draft.invoice_registration_no) {
    const summary = transitionalSummary(
      draft.tax_groups.reduce((sum, g) => sum + Number(g.tax), 0),
      issueDate,
    );
    // 🔴 帳票の日付は店の国で決める（Step 3）＝この節は JP の店にしか出ないので
    //    常に和風固定。UI ロケールが en でも仕入先向け書類の日付は変えない。
    const nextClause = summary.measure.next
      ? t("{date}以降は控除割合が{rate}%へ引き下げられます。", {
          date: escapeHtml(formatJpDateFixed(summary.measure.next.startDate)),
          rate: summary.measure.next.ratePercent,
        })
      : "";
    const body = t(
      "※仕入先は適格請求書発行事業者として未登録のため、仕入税額控除は経過措置の対象です。支払消費税 {total} のうち控除対象は{rate}%（{deductible}）、控除対象外 {nonDeductible}（発行日 {date} 基準）。",
      {
        total: yen(summary.totalTax),
        rate: summary.measure.ratePercent,
        deductible: yen(summary.split.deductible),
        nonDeductible: yen(summary.split.nonDeductible),
        date: escapeHtml(formatJpDateFixed(issueDate)),
      },
    );
    transitionalNote = `<p class="note">${body}${nextClause}</p>`;
  }

  return `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${t("発注書")} ${escapeHtml(draft.supplier_name)} ${escapeHtml(today)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: "Hiragino Sans", "Yu Gothic", "Noto Sans JP", sans-serif; color: #1a1a1a; margin: 0; padding: 24px; }
  h1 { font-size: 22px; margin: 0 0 4px; letter-spacing: 0.1em; }
  .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 16px; }
  .meta { text-align: right; font-size: 12px; line-height: 1.6; }
  .meta .issuer { margin-top: 8px; }
  .meta .issuer .name { font-size: 14px; font-weight: 700; }
  .supplier { font-size: 14px; line-height: 1.7; margin-bottom: 16px; }
  .supplier .name { font-size: 18px; font-weight: 700; }
  .warn { color: #b45309; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { border: 1px solid #c4c4c4; padding: 6px 8px; text-align: left; }
  th { background: #f4f4f4; }
  td.num, th.num { text-align: right; }
  .est { color: #6b7280; font-size: 10px; }
  .summary { margin-top: 16px; margin-left: auto; width: 320px; font-size: 13px; }
  .summary .row { display: flex; justify-content: space-between; padding: 4px 0; }
  .summary .sub { color: #6b7280; }
  .summary .total { border-top: 2px solid #1a1a1a; margin-top: 6px; padding-top: 8px; font-size: 16px; font-weight: 700; }
  .note { font-size: 11px; color: #6b7280; margin-top: 24px; }
  .notebox { clear: both; margin-top: 24px; border: 1px solid #c4c4c4; padding: 8px 10px; font-size: 12px; white-space: pre-wrap; }
  .notebox-label { font-weight: 700; margin-bottom: 4px; }
  @media print { body { padding: 0; } @page { size: A4; margin: 16mm; } }
</style>
</head>
<body>
  <div class="header">
    <div><h1>${t("発注書")}</h1></div>
    <div class="meta">
      <div>${t("発行日")}: ${escapeHtml(issueDate)}</div>
      ${metaExtra}
      <div class="issuer"><div class="issuer-label">${t("発注元")}</div>${issuerBlock(company, registration)}</div>
    </div>
  </div>

  <div class="supplier">
    <div class="name">${t("{supplier}　御中", { supplier: escapeHtml(draft.supplier_name) })}</div>
    ${invoiceLine}
  </div>

  <table>
    <thead>
      <tr>
        <th>SKU</th><th>${t("商品名")}</th><th class="num">${t("在庫")}</th><th class="num">${t("発注数")}</th>
        <th class="num">${t("仕入単価")}</th>${hasTax ? `<th>${t("税区分")}</th>` : ""}<th class="num">${t("小計")}</th>
      </tr>
    </thead>
    <tbody>${lineRows(draft, hasTax)}</tbody>
  </table>

  <div class="summary">
${draft.tax_groups
      .map(
        (g) => `<div class="row"><span>${escapeHtml(g.name)}</span><span>${yen(g.subtotal)}</span></div>
    <div class="row sub"><span>　${t("うち税")}</span><span>${yen(g.tax)}</span></div>`,
      )
      .join("\n    ")}
    <div class="row total"><span>${hasTax ? t("合計（税込）") : t("合計")}</span><span>${yen(draft.total_amount)}</span></div>
  </div>
  ${noteBlock}
  ${estimatedNote}
  ${transitionalNote}
</body>
</html>`;
}
